import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Paper, Typography, useTheme, alpha } from '@mui/material';
import 'chart.js/auto';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TimeSeriesChartProps {
  title: string;
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor: string;
      backgroundColor: string;
    }[];
  };
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ title, data }) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 800,
      easing: 'easeOutQuad' as const,
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: theme.typography.fontFamily,
            size: 12
          },
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        boxPadding: 4,
        usePointStyle: true,
        callbacks: {
          label: function(context: any) {
            const label = context.dataset.label === 'Volume' ? 'Объем' : 'Минимальная цена';
            return ` ${label}: ${context.parsed.y.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        grid: {
          color: alpha(theme.palette.divider, 0.1),
          drawBorder: false,
        },
        ticks: {
          font: {
            family: theme.typography.fontFamily,
            size: 11
          },
          color: theme.palette.text.secondary,
          padding: 8
        }
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            family: theme.typography.fontFamily,
            size: 11
          },
          color: theme.palette.text.secondary,
          padding: 8,
          maxRotation: 45,
          minRotation: 45
        }
      },
    },
    elements: {
      point: {
        radius: 2,
        hoverRadius: 6,
        borderWidth: 2,
      },
      line: {
        tension: 0.2,
        borderWidth: 2,
        fill: true,
      }
    }
  };

  // Translate dataset labels to Russian
  const enhancedData = {
    ...data,
    datasets: data.datasets.map((dataset) => {
      const baseColor = dataset.borderColor;
      return {
        ...dataset,
        label: dataset.label === 'Volume' ? 'Объем' : 'Минимальная цена',
        backgroundColor: alpha(baseColor, 0.1),
        borderColor: baseColor,
        fill: true,
        pointBackgroundColor: baseColor,
        pointBorderColor: theme.palette.background.paper,
        pointHoverBackgroundColor: theme.palette.background.paper,
        pointHoverBorderColor: baseColor,
        pointHoverBorderWidth: 3,
        tension: 0.2
      };
    })
  };

  return (
    <Paper 
      sx={{ 
        p: 3, 
        height: 400, 
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <Typography variant="h6" fontWeight="600" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ height: 'calc(100% - 40px)', pt: 1 }}>
        <Line options={options} data={enhancedData} />
      </Box>
    </Paper>
  );
};

export default TimeSeriesChart; 